import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalProps,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  ListGroup,
  ListGroupItem,
  Spinner
} from 'reactstrap';
import { offlineQueueService } from '../../services/offline-queue.service';
import { pushNotificationService, PushNotificationPayload } from '../../services/push-notification.service';
import { installPromptService } from '../../services/install-prompt.service';
import { advancedCacheService, CacheConfig } from '../../services/advanced-cache.service';
import { mobileWidgetService, WidgetConfig, AppShortcut, MobileFeatures } from '../../services/mobile-widget.service';

interface SyncTimingConfigProps {
  modalProps?: ModalProps;
  onCancel?: () => void;
  storeId?: string;
}

export const SyncTimingConfig: React.FC<SyncTimingConfigProps> = ({ 
  modalProps = { size: 'xl', isOpen: true, className: 'sync-timing-config' }, 
  onCancel,
  storeId 
}) => {
  const [activeTab, setActiveTab] = useState('1');
  const [syncConfig, setSyncConfig] = useState({
    immediate: true,
    intervals: [30000, 120000, 300000], // 30s, 2min, 5min
    maxAge: 3600000, // 1 hour
    batchSize: 10,
    retryIntervals: [5000, 30000, 120000] // 5s, 30s, 2min
  });
  const [isApplying, setIsApplying] = useState(false);
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [queueStats, setQueueStats] = useState<any>(null);

  // Push Notification States
  const [pushNotificationStatus, setPushNotificationStatus] = useState<any>(null);
  const [showTestNotification, setShowTestNotification] = useState(false);
  const [testNotificationPayload, setTestNotificationPayload] = useState<PushNotificationPayload>({
    title: 'Test Notification',
    body: 'This is a test push notification',
    icon: '/assets/icons/android-icon-192x192.png'
  });

  // Install Prompt States
  const [installPromptStatus, setInstallPromptStatus] = useState<any>(null);

  // Cache States
  const [cacheStats, setCacheStats] = useState<any[]>([]);
  const [cacheSizeInfo, setCacheSizeInfo] = useState<any>(null);
  const [showCacheModal, setShowCacheModal] = useState(false);
  const [newCacheConfig, setNewCacheConfig] = useState<Partial<CacheConfig>>({
    name: '',
    strategy: { name: 'NetworkFirst' },
    patterns: []
  });

  // Mobile Widget States
  const [mobileFeatures, setMobileFeatures] = useState<MobileFeatures | null>(null);
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);
  const [shortcuts, setShortcuts] = useState<AppShortcut[]>([]);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [showShortcutModal, setShowShortcutModal] = useState(false);
  const [newWidget, setNewWidget] = useState<Partial<WidgetConfig>>({
    id: '',
    name: '',
    description: '',
    icon: '',
    size: 'medium',
    url: '',
    updateInterval: 15
  });
  const [newShortcut, setNewShortcut] = useState<Partial<AppShortcut>>({
    name: '',
    shortName: '',
    description: '',
    icon: '',
    url: '',
    color: '#667eea'
  });

  const { t } = useTranslation(['settings', 'common']);

  useEffect(() => {
    // Initialize sync status
    updateSyncStatus();
    updateQueueStats();

    // Initialize PWA features
    updatePushNotificationStatus();
    updateInstallPromptStatus();
    updateCacheStats();
    updateMobileFeatures();
    updateWidgets();
    updateShortcuts();

    // Set up subscriptions
    const unsubscribeInstall = installPromptService.subscribe(setInstallPromptStatus);

    // Set up periodic updates
    const interval = setInterval(() => {
      updateSyncStatus();
      updateQueueStats();
      updateCacheStats();
    }, 10000); // Update every 10 seconds

    return () => {
      unsubscribeInstall();
      clearInterval(interval);
    };
  }, []);

  // Sync timing functions
  const updateSyncStatus = () => {
    setSyncStatus(offlineQueueService.getSyncStatus());
  };

  const updateQueueStats = () => {
    setQueueStats(offlineQueueService.getQueueStats());
  };

  // Push Notification functions
  const updatePushNotificationStatus = () => {
    setPushNotificationStatus(pushNotificationService.getSubscriptionStatus());
  };

  // Install Prompt functions
  const updateInstallPromptStatus = () => {
    setInstallPromptStatus(installPromptService.getInstallPromptData());
  };

  // Cache functions
  const updateCacheStats = async () => {
    const stats = await advancedCacheService.getCacheStats();
    const sizeInfo = await advancedCacheService.getCacheSizeInfo();
    setCacheStats(stats);
    setCacheSizeInfo(sizeInfo);
  };

  // Mobile Widget functions
  const updateMobileFeatures = () => {
    setMobileFeatures(mobileWidgetService.getMobileFeatures());
  };

  const updateWidgets = () => {
    setWidgets(mobileWidgetService.getWidgets());
  };

  const updateShortcuts = () => {
    setShortcuts(mobileWidgetService.getAppShortcuts());
  };

  const handleConfigChange = (field: string, value: any) => {
    setSyncConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleIntervalsChange = (index: number, value: number) => {
    const newIntervals = [...syncConfig.intervals];
    newIntervals[index] = value * 1000; // Convert to milliseconds
    setSyncConfig(prev => ({
      ...prev,
      intervals: newIntervals
    }));
  };

  const handleRetryIntervalsChange = (index: number, value: number) => {
    const newRetryIntervals = [...syncConfig.retryIntervals];
    newRetryIntervals[index] = value * 1000; // Convert to milliseconds
    setSyncConfig(prev => ({
      ...prev,
      retryIntervals: newRetryIntervals
    }));
  };

  const applySyncConfig = async () => {
    setIsApplying(true);
    try {
      offlineQueueService.configureSyncTiming(syncConfig);
      updateSyncStatus();
      updateQueueStats();
      console.log('Sync configuration applied successfully');
    } catch (error) {
      console.error('Failed to apply sync configuration:', error);
    } finally {
      setIsApplying(false);
    }
  };

  const forceSyncNow = async () => {
    try {
      await offlineQueueService.forceSyncNow();
      updateSyncStatus();
      updateQueueStats();
    } catch (error) {
      console.error('Failed to force sync:', error);
    }
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  };

  const formatTimeRemaining = (ms: number) => {
    if (ms <= 0) return 'Now';
    return formatTime(ms);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Push Notification handlers
  const handleSubscribePush = async () => {
    try {
      await pushNotificationService.subscribe();
      updatePushNotificationStatus();
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
    }
  };

  const handleUnsubscribePush = async () => {
    try {
      await pushNotificationService.unsubscribe();
      updatePushNotificationStatus();
    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
    }
  };

  const handleTestNotification = async () => {
    try {
      await pushNotificationService.testNotification();
      setShowTestNotification(false);
    } catch (error) {
      console.error('Failed to show test notification:', error);
    }
  };

  // Install Prompt handlers
  const handleShowInstallPrompt = async () => {
    try {
      const result = await installPromptService.showInstallPrompt();
      if (result) {
        console.log('Install prompt accepted');
      }
    } catch (error) {
      console.error('Failed to show install prompt:', error);
    }
  };

  const handleShowCustomInstallBanner = () => {
    installPromptService.showCustomInstallBanner({
      title: 'Install eSpa247 POS',
      description: 'Get quick access to the app from your home screen',
      icon: '/assets/icons/android-icon-48x48.png',
      buttonText: 'Install Now'
    });
  };

  // Cache handlers
  const handleClearCache = async (cacheName?: string) => {
    try {
      if (cacheName) {
        await advancedCacheService.clearCache(cacheName);
      } else {
        await advancedCacheService.clearAllCaches();
      }
      updateCacheStats();
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  };

  const handleAddCacheConfig = () => {
    if (newCacheConfig.name && newCacheConfig.strategy) {
      advancedCacheService.addCacheConfig(newCacheConfig as CacheConfig);
      setNewCacheConfig({ name: '', strategy: { name: 'NetworkFirst' }, patterns: [] });
      updateCacheStats();
    }
  };

  // Mobile Widget handlers
  const handleAddWidget = () => {
    if (newWidget.id && newWidget.name && newWidget.url) {
      mobileWidgetService.addWidget(newWidget as WidgetConfig);
      setNewWidget({
        id: '',
        name: '',
        description: '',
        icon: '',
        size: 'medium',
        url: '',
        updateInterval: 15
      });
      updateWidgets();
      setShowWidgetModal(false);
    }
  };

  const handleAddShortcut = () => {
    if (newShortcut.name && newShortcut.url) {
      mobileWidgetService.addAppShortcut(newShortcut as AppShortcut);
      setNewShortcut({
        name: '',
        shortName: '',
        description: '',
        icon: '',
        url: '',
        color: '#667eea'
      });
      updateShortcuts();
      setShowShortcutModal(false);
    }
  };

  const formatBytes = (bytes: number | string): string => {
    if (typeof bytes === 'string') return bytes; // Handle 'N/A' values
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const content = (
    <Container fluid className="sync-timing-config">
      <div className="mb-4 text-left">
        <p className="text-muted">
          {t('pwa.sync_timing_config_description')}
        </p>
      </div>

      {/* Navigation Tabs */}
      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink
            className={activeTab === '1' ? 'active' : ''}
            onClick={() => setActiveTab('1')}
          >
            <i className="cil-alarm mr-2" />
            {t('pwa.sync_timing')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === '2' ? 'active' : ''}
            onClick={() => setActiveTab('2')}
          >
            <i className="cil-info mr-2" />
            {t('pwa.push_notifications')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === '3' ? 'active' : ''}
            onClick={() => setActiveTab('3')}
          >
            <i className="icon-cloud-download mr-2" />
            {t('pwa.install_prompts')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === '4' ? 'active' : ''}
            onClick={() => setActiveTab('4')}
          >
            <i className="cil-money mr-2" />
            {t('pwa.advanced_caching')}
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={activeTab === '5' ? 'active' : ''}
            onClick={() => setActiveTab('5')}
          >
            <i className="cil-pos mr-2" />
            {t('pwa.mobile_widgets')}
          </NavLink>
        </NavItem>
      </Nav>

      {/* Tab Content */}
      <TabContent activeTab={activeTab}>
        {/* Tab 1: Sync Timing */}
        <TabPane tabId="1">
          {/* Current Status */}
          {syncStatus && (
            <Row className="mb-4">
              <Col>
                <Card>
                  <CardHeader className="text-left">
                    <h6 className="mb-0 d-flex align-items-center justify-content-left">
                      <i className="cil-clock mr-2" />
                      {t('pwa.sync_status')}
                    </h6>
                  </CardHeader>
                  <CardBody>
                    <Row className="text-left status-card">
                      <Col md={3} xs={6}>
                        <div className="h5 mb-0 d-flex align-items-center justify-content-center">
                          <Badge color={syncStatus.isOnline ? 'success' : 'danger'}>
                            {syncStatus.isOnline ? t('pwa.online') : t('pwa.offline')}
                          </Badge>
                        </div>
                        <small className="text-muted">{t('pwa.connection')}</small>
                      </Col>
                      <Col md={3} xs={6}>
                        <div className="h5 mb-0 d-flex align-items-center justify-content-center">
                          {syncStatus.activeIntervals}
                        </div>
                        <small className="text-muted">{t('pwa.active_timers')}</small>
                      </Col>
                      <Col md={3} xs={6}>
                        <div className="h5 mb-0 d-flex align-items-center justify-content-center">
                          {syncStatus.pendingRetries}
                        </div>
                        <small className="text-muted">{t('pwa.pending_retries')}</small>
                      </Col>
                      <Col md={3} xs={6}>
                        <div className="h5 mb-0 d-flex align-items-center justify-content-center">
                          {formatTimeRemaining(syncStatus.nextSyncEstimate)}
                        </div>
                        <small className="text-muted">{t('pwa.next_sync')}</small>
                      </Col>
                    </Row>

                    {syncStatus.lastSyncTime > 0 && (
                      <div className="mt-3 text-left">
                        <small className="text-muted">
                          {t('pwa.last_sync')}: {new Date(syncStatus.lastSyncTime).toLocaleString()}
                        </small>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {/* Queue Stats */}
          {queueStats && (
            <Row className="mb-4">
              <Col>
                <Alert color="info">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-center flex-grow-1">
                      <strong>{t('pwa.queue_status')}:</strong> {queueStats.totalRequests} {t('pwa.total')}, {queueStats.pendingRequests} {t('pwa.pending')}, {queueStats.failedRequests} {t('pwa.failed')}
                    </div>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={forceSyncNow}
                      disabled={!syncStatus?.isOnline}
                      className="d-flex align-items-center"
                    >
                      <i className="cil-loop-circular icons w-icon-15 c-icon-white mr-2" />
                      {t('pwa.force_sync_now')}
                    </Button>
                  </div>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Sync Configuration */}
          <Row className="mb-4">
            <Col md={4}>
              <Card>
                <CardHeader className="text-left">
                  <h6 className="mb-0 d-flex align-items-center justify-content-left">
                    <i className="cil-settings mr-2" />
                    {t('pwa.sync_settings')}
                  </h6>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label className="d-flex align-items-center justify-content-left">
                      <Input
                        type="checkbox"
                        checked={syncConfig.immediate}
                        onChange={(e) => handleConfigChange('immediate', e.target.checked)}
                        className="ml-1"
                      />
                      <span className="ml-4">{t('pwa.sync_immediate')}</span>
                    </Label>
                    <small className="form-text text-muted">
                      {t('pwa.sync_immediate_description')}
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label for="batchSize" className="d-flex align-items-center justify-content-left">
                      <i className="cil-settings  mr-2" />
                      {t('pwa.sync_batch_size')}
                    </Label>
                    <Input
                      id="batchSize"
                      type="number"
                      value={syncConfig.batchSize}
                      onChange={(e) => handleConfigChange('batchSize', parseInt(e.target.value))}
                      min="1"
                      max="50"
                    />
                    <small className="form-text text-muted">
                      {t('pwa.number_of_requests_to_process_in_each_sync_batch')}
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label for="maxAge" className="d-flex align-items-center justify-content-left">
                      <i className="cil-clock mr-2" />
                      {t('pwa.sync_max_age')}
                    </Label>
                    <Input
                      id="maxAge"
                      type="number"
                      value={Math.round(syncConfig.maxAge / 3600000)}
                      onChange={(e) => handleConfigChange('maxAge', parseInt(e.target.value) * 3600000)}
                      min="1"
                      max="24"
                    />
                    <small className="form-text text-muted">
                      {t('pwa.automatically_remove_requests_older_than_this')}
                    </small>
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            <Col md={4}>
              <Card>
                <CardHeader className="text-left">
                  <h6 className="mb-0 d-flex align-items-center justify-content-left">
                    <i className="cil-alarm mr-2" />
                    {t('pwa.sync_intervals')}
                  </h6>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-3 text-left">
                    {t('pwa.multiple_sync_timers_for_redundancy_in_seconds')}
                  </p>
                  
                  {syncConfig.intervals.map((interval, index) => (
                    <FormGroup key={index}>
                      <Label for={`interval-${index}`} className="d-flex align-items-center">
                        <i className="cil-alarm mr-2" />
                        {t(`pwa.sync_intervals`, { number: index + 1 })}
                      </Label>
                      <Input
                        id={`interval-${index}`}
                        type="number"
                        value={Math.round(interval / 1000)}
                        onChange={(e) => handleIntervalsChange(index, parseInt(e.target.value))}
                        min="5"
                        max="3600"
                      />
                      <small className="form-text text-muted text-left d-block">
                        {t('pwa.current')}: {formatTime(interval)}
                      </small>
                    </FormGroup>
                  ))}
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Row className="mb-4">
                <Col>
                  <Card>
                    <CardHeader className="text-left">
                      <h6 className="mb-0 d-flex align-items-center justify-content-left">
                        <i className="cil-cloud mr-2" />
                        {t('pwa.sync_retry_intervals')}
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <p className="text-muted mb-3 text-left">
                        {t('pwa.sync_retry_intervals_description')}
                      </p>
                      {syncConfig.retryIntervals.map((interval, index) => (
                        <Row key={index}>
                          <Col md={12}>
                            <FormGroup>
                              <Label for={`retry-${index}`} className="d-flex align-items-center justify-content-left">
                                <i className="cil-cloud mr-2" />
                                {t(`pwa.retry_interval_1`, { number: index + 1 })}
                              </Label>
                              <Input
                                id={`retry-${index}`}
                                type="number"
                                value={Math.round(interval / 1000)}
                                onChange={(e) => handleRetryIntervalsChange(index, parseInt(e.target.value))}
                                min="1"
                                max="300"
                              />
                              <small className="form-text text-muted text-left d-block">
                                {formatTime(interval)}
                              </small>
                            </FormGroup>
                          </Col>
                        </Row>
                      ))}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
          {/* How It Works */}
          <Row>
            <Col>
              <Card>
                <CardHeader className="text-left">
                  <h6 className="mb-0 d-flex align-items-center justify-content-left">
                    <i className="cil-info mr-2" />
                    {t('pwa.how_sync_timing_works')}
                  </h6>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <Alert color="info">
                        <h6 className="text-left">{t('pwa.sync_strategies')}:</h6>
                        <ul className="mb-2">
                          <li><strong>{t('pwa.immediate_sync')}:</strong> {t('pwa.immediate_sync_description')}</li>
                          <li><strong>{t('pwa.multiple_intervals')}:</strong> {t('pwa.multiple_intervals_description')}</li>
                          <li><strong>{t('pwa.visibility_sync')}:</strong> {t('pwa.visibility_sync_description')}</li>
                          <li><strong>{t('pwa.smart_retry')}:</strong> {t('pwa.smart_retry_description')}</li>
                          <li><strong>{t('pwa.batch_processing')}:</strong> {t('pwa.batch_processing_description')}</li>
                          <li><strong>{t('pwa.auto_cleanup')}:</strong> {t('pwa.auto_cleanup_description')}</li>
                        </ul>
                      </Alert>
                    </Col>
                    <Col md={6}>
                      <Alert color="warning">
                        <h6 className="text-left">{t('pwa.best_practices')}:</h6>
                        <ul className="mb-0">
                          <li>{t('pwa.keep_intervals_reasonable')}</li>
                          <li>{t('pwa.use_small_batch_sizes')}</li>
                          <li>{t('pwa.set_appropriate_max_age')}</li>
                          <li>{t('pwa.configure_retry_intervals')}</li>
                        </ul>
                      </Alert>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* Tab 2: Push Notifications */}
        <TabPane tabId="2">
          {/* Status Overview */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={pushNotificationStatus?.supported ? 'success' : 'danger'}>
                      {pushNotificationStatus?.supported ? t('pwa.supported') : t('pwa.not_supported')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.browser_support')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={pushNotificationStatus?.permission?.granted ? 'success' : 'warning'}>
                      {pushNotificationStatus?.permission?.granted ? t('pwa.granted') : t('pwa.not_granted')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.permission_status')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={pushNotificationStatus?.subscribed ? 'success' : 'secondary'}>
                      {pushNotificationStatus?.subscribed ? t('pwa.subscribed') : t('pwa.not_subscribed')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.subscription_status')}</small>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Actions */}
          <Card className="mb-4">
            <CardHeader>
              <h6 className="mb-0">
                <i className="cil-https c-icon-white mr-2" />
                {t('pwa.push_notification_actions')}
              </h6>
            </CardHeader>
            <CardBody>
              <div className="d-flex gap-2 flex-wrap">
                {!pushNotificationStatus?.permission?.granted && (
                  <Button
                    color="primary"
                    onClick={handleSubscribePush}
                    disabled={!pushNotificationStatus?.supported}
                     className='mr-2'
                  >
                    <i className="cil-https c-icon-white mr-2" />
                    {t('pwa.request_permission')}
                  </Button>
                )}
                
                {pushNotificationStatus?.permission?.granted && !pushNotificationStatus?.subscribed && (
                  <Button
                    color="success"
                    onClick={handleSubscribePush}
                  >
                    <i className="cil-check c-icon-white mr-2" />
                    {t('pwa.subscribe_to_notifications')}
                  </Button>
                )}
                
                {pushNotificationStatus?.subscribed && (
                  <Button
                    color="danger"
                    onClick={handleUnsubscribePush}
                  >
                    <i className="cil-x c-icon-white mr-2" />
                    {t('pwa.unsubscribe')}
                  </Button>
                )}
                
                <Button
                  color="info"
                  onClick={() => setShowTestNotification(true)}
                  disabled={!pushNotificationStatus?.permission?.granted}
                >
                  <i className="cil-paper-plane c-icon-white mr-2" />
                  {t('pwa.test_notification')}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Subscription Details */}
          {pushNotificationStatus?.subscribed && (
            <Card className="mb-4">
              <CardHeader>
                <h6 className="mb-0">
                  <i className="cil-info c-icon-white mr-2" />
                  {t('pwa.subscription_details')}
                </h6>
              </CardHeader>
              <CardBody>
                <div className="row">
                  <div className="col-md-6">
                    <strong>{t('pwa.endpoint')}:</strong><br />
                    <small className="text-muted text-break">
                      {pushNotificationService.getSubscriptionData()?.endpoint}
                    </small>
                  </div>
                  <div className="col-md-6">
                    <strong>{t('pwa.keys')}:</strong><br />
                    <small className="text-muted">
                      P256DH: {pushNotificationService.getSubscriptionData()?.keys.p256dh.substring(0, 20)}...<br />
                      Auth: {pushNotificationService.getSubscriptionData()?.keys.auth.substring(0, 20)}...
                    </small>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </TabPane>

        {/* Tab 3: Install Prompts */}
        <TabPane tabId="3">
          {/* Status Overview */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={installPromptStatus?.isSupported ? 'success' : 'danger'}>
                      {installPromptStatus?.isSupported ? t('pwa.supported') : t('pwa.not_supported')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.install_support')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={installPromptStatus?.canInstall ? 'success' : 'secondary'}>
                      {installPromptStatus?.canInstall ? t('pwa.can_install') : t('pwa.cannot_install')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.install_available')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={installPromptStatus?.isInstalled ? 'success' : 'warning'}>
                      {installPromptStatus?.isInstalled ? t('pwa.installed') : t('pwa.not_installed')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.install_status')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={installPromptStatus?.isStandalone ? 'success' : 'info'}>
                      {installPromptStatus?.isStandalone ? t('pwa.standalone') : t('pwa.browser')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.display_mode')}</small>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Actions */}
          <Card className="mb-4">
            <CardHeader>
              <h6 className="mb-0">
                <i className="icon-cloud-download mr-2" />
                {t('pwa.install_actions')}
              </h6>
            </CardHeader>
            <CardBody>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  color="primary"
                  onClick={handleShowInstallPrompt}
                  disabled={!installPromptStatus?.canInstall}
                  className='mr-2'
                >
                  <i className="icon-cloud-download c-icon-white mr-2" />
                  {t('pwa.show_install_prompt')}
                </Button>
                
                <Button
                  color="success"
                  onClick={handleShowCustomInstallBanner}
                  disabled={installPromptStatus?.isInstalled}
                   className='mr-2'
                >
                  <i className="cil-check-in c-icon-white mr-2" />
                  {t('pwa.show_custom_banner')}
                </Button>
                
                <Button
                  color="info"
                  onClick={() => installPromptService.enableAutoShow(5000)}
                  disabled={!installPromptStatus?.isSupported}
                   className='mr-2'
                >
                  <i className="cil-clock c-icon-white mr-2" />
                  {t('pwa.enable_auto_show_5s')}
                </Button>
                
                <Button
                  color="warning"
                  onClick={() => installPromptService.disableAutoShow()}
                >
                  <i className="cil-ban c-icon-white mr-2" />
                  {t('pwa.disable_auto_show')}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Platform Information */}
          <Card className="mb-4">
            <CardHeader>
              <h6 className="mb-0">
                <i className="cil-pos mr-2" />
                {t('pwa.platform_information')}
              </h6>
            </CardHeader>
            <CardBody>
              <div className="row">
                <div className="col-md-6">
                  <strong>{t('pwa.device_type')}:</strong> {installPromptService.isMobileDevice() ? t('pwa.mobile') : t('pwa.desktop')}<br />
                  <strong>{t('pwa.platform')}:</strong> {installPromptService.isAndroid() ? 'Android' : installPromptService.isIOS() ? 'iOS' : t('pwa.other')}<br />
                  <strong>{t('pwa.install_instructions')}:</strong><br />
                  <small className="text-muted">{installPromptService.getInstallInstructions()}</small>
                </div>
                <div className="col-md-6">
                  <strong>{t('pwa.auto_show_status')}:</strong> {installPromptService.getInstallStats().autoShowEnabled ? t('pwa.enabled') : t('pwa.disabled')}<br />
                  <strong>{t('pwa.show_delay')}:</strong> {installPromptService.getInstallStats().showDelay}ms<br />
                  <strong>{t('pwa.has_prompt')}:</strong> {installPromptStatus?.deferredPrompt ? t('pwa.yes') : t('pwa.no')}
                </div>
              </div>
            </CardBody>
          </Card>
        </TabPane>

        {/* Tab 4: Advanced Caching */}
        <TabPane tabId="4">
          {/* Cache Overview */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={advancedCacheService.isCachingSupported() ? 'success' : 'danger'}>
                      {advancedCacheService.isCachingSupported() ? t('pwa.supported') : t('pwa.not_supported')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.caching_support')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    {cacheSizeInfo?.cacheCount || 0}
                  </div>
                  <small className="text-muted">{t('pwa.active_caches')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    {cacheSizeInfo?.totalEntries || 0}
                  </div>
                  <small className="text-muted">{t('pwa.total_entries')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    {formatBytes(cacheSizeInfo?.totalSize || 0)}
                  </div>
                  <small className="text-muted">{t('pwa.total_size')}</small>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Cache Management */}
          <Card className="mb-4">
            <CardHeader>
              <h6 className="mb-0">
                <i className="cil-settings mr-2" />
                {t('pwa.cache_management')}
              </h6>
            </CardHeader>
            <CardBody>
              <div className="d-flex gap-2 flex-wrap">
                <Button
                  color="danger"
                  onClick={() => handleClearCache()}
                   className='mr-2'
                >
                  <i className="cil-trash c-icon-white mr-2" />
                  {t('pwa.clear_all_caches')}
                </Button>
                
                <Button
                  color="info"
                  onClick={() => setShowCacheModal(true)}
                  className='mr-2'
                >
                  <i className="cil-plus c-icon-white mr-2" />
                  {t('pwa.add_cache_config')}
                </Button>
                
                <Button
                  color="secondary"
                  onClick={updateCacheStats}
                >
                  <i className="cil-aperture c-icon-white mr-2" />
                  {t('pwa.refresh_stats')}
                </Button>
              </div>
            </CardBody>
          </Card>

          {/* Cache Statistics */}
          <Card className="mb-4">
            <CardHeader>
              <h6 className="mb-0">
                <i className="cil-spreadsheet mr-2" />
                {t('pwa.cache_statistics')}
              </h6>
            </CardHeader>
            <CardBody>
              <ListGroup>
                {cacheStats.map((stat, index) => (
                  <ListGroupItem key={index} className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{stat.name}</strong><br />
                      <small className="text-muted">
                        {stat.entries} {t('pwa.entries')} • {t('pwa.size')}: N/A ({t('pwa.performance_optimized')}) • 
                        {t('pwa.hit_rate')}: {stat.hitRate} • {t('pwa.miss_rate')}: {stat.missRate}
                      </small>
                    </div>
                    <div>
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => handleClearCache(stat.name)}
                      >
                        <i className="cil-trash" />
                      </Button>
                    </div>
                  </ListGroupItem>
                ))}
              </ListGroup>
            </CardBody>
          </Card>
        </TabPane>

        {/* Tab 5: Mobile Widgets */}
        <TabPane tabId="5">
          {/* Mobile Features Overview */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    <Badge color={mobileFeatures?.isMobile ? 'success' : 'info'}>
                      {mobileFeatures?.isMobile ? t('pwa.mobile') : t('pwa.desktop')}
                    </Badge>
                  </div>
                  <small className="text-muted">{t('pwa.device_type')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    {widgets.length}
                  </div>
                  <small className="text-muted">{t('pwa.active_widgets')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                  <div className="h4 mb-2">
                    {shortcuts.length}
                  </div>
                  <small className="text-muted">{t('pwa.app_shortcuts')}</small>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <CardBody>
                    <div className="h4 mb-2">
                      <Badge color={mobileFeatures?.displayMode === 'standalone' ? 'success' : 'warning'}>
                        {mobileFeatures?.displayMode === 'standalone' ? t('pwa.standalone') : t('pwa.browser')}
                      </Badge>
                    </div>
                    <small className="text-muted">{t('pwa.display_mode')}</small>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Widgets Management */}
          <Row className="mb-4">
            <Col md={6}>
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <i className="cil-check-in mr-2" />
                      {t('pwa.widgets')}
                    </h6>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setShowWidgetModal(true)}
                    >
                      <i className="cil-plus c-icon-white mr-2" />
                      {t('pwa.add_widget')}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <ListGroup>
                    {widgets.map((widget, index) => (
                      <ListGroupItem key={index} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{widget.name}</strong><br />
                          <small className="text-muted">
                            {widget.description} • {t('pwa.size')}: {widget.size} • {t('pwa.update_interval')}: {widget.updateInterval}s
                          </small>
                        </div>
                        <div>
                          <Button
                            color="warning"
                            size="sm"
                            onClick={() => {
                              mobileWidgetService.removeWidget(widget.id);
                              updateWidgets();
                            }}
                          >
                            <i className="cil-trash" />
                          </Button>
                        </div>
                      </ListGroupItem>
                    ))}
                    {widgets.length === 0 && (
                      <ListGroupItem className="text-center text-muted">
                        {t('pwa.no_widgets_configured')}
                      </ListGroupItem>
                    )}
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      <i className="cil-bookmark mr-2" />
                      {t('pwa.app_shortcuts')}
                    </h6>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setShowShortcutModal(true)}
                    >
                      <i className="cil-plus c-icon-white mr-2" />
                      {t('pwa.add_shortcut')}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  <ListGroup>
                    {shortcuts.map((shortcut, index) => (
                      <ListGroupItem key={index} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{shortcut.name}</strong><br />
                          <small className="text-muted">
                            {shortcut.description} • {t('pwa.short_name')}: {shortcut.shortName}
                          </small>
                        </div>
                        <div>
                          <Button
                            color="warning"
                            size="sm"
                            onClick={() => {
                              mobileWidgetService.removeAppShortcut(shortcut.name);
                              updateShortcuts();
                            }}
                          >
                            <i className="cil-trash" />
                          </Button>
                        </div>
                      </ListGroupItem>
                    ))}
                    {shortcuts.length === 0 && (
                      <ListGroupItem className="text-center text-muted">
                        {t('pwa.no_shortcuts_configured')}
                      </ListGroupItem>
                    )}
                  </ListGroup>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Mobile Features Status */}
          <Card>
            <CardHeader>
              <h6 className="mb-0">
                <i className="cil-pos mr-2" />
                {t('pwa.mobile_features_status')}
              </h6>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md={6}>
                  <ListGroup>
                    <ListGroupItem className="d-flex justify-content-between">
                      <span>{t('pwa.service_worker')}</span>
                      <Badge color="success">{t('pwa.active')}</Badge>
                    </ListGroupItem>
                    <ListGroupItem className="d-flex justify-content-between">
                      <span>{t('pwa.offline_queue')}</span>
                      <Badge color="info">{t('pwa.enabled')}</Badge>
                    </ListGroupItem>
                    <ListGroupItem className="d-flex justify-content-between">
                      <span>{t('pwa.background_sync')}</span>
                      <Badge color="success">{t('pwa.enabled')}</Badge>
                    </ListGroupItem>
                  </ListGroup>
                </Col>
                <Col md={6}>
                  <ListGroup>
                    <ListGroupItem className="d-flex justify-content-between">
                      <span>{t('pwa.push_notifications')}</span>
                      <Badge color={pushNotificationStatus?.supported ? 'success' : 'warning'}>
                        {pushNotificationStatus?.supported ? t('pwa.available') : t('pwa.not_available')}
                      </Badge>
                    </ListGroupItem>
                    <ListGroupItem className="d-flex justify-content-between">
                      <span>{t('pwa.install_prompts')}</span>
                      <Badge color={installPromptStatus?.isSupported ? 'success' : 'warning'}>
                        {installPromptStatus?.isSupported ? t('pwa.available') : t('pwa.not_available')}
                      </Badge>
                    </ListGroupItem>
                    <ListGroupItem className="d-flex justify-content-between">
                      <span>{t('pwa.advanced_caching')}</span>
                      <Badge color={advancedCacheService.isCachingSupported() ? 'success' : 'warning'}>
                        {advancedCacheService.isCachingSupported() ? t('pwa.available') : t('pwa.not_available')}
                      </Badge>
                    </ListGroupItem>
                  </ListGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </TabPane>
      </TabContent>
    </Container>
  );

  // Test Notification Modal
  const testNotificationModal = (
    <Modal isOpen={showTestNotification} toggle={() => setShowTestNotification(false)}>
      <ModalHeader toggle={() => setShowTestNotification(false)}>
        <i className="cil-paper-plane c-icon-white mr-2" />
        {t('pwa.test_push_notification')}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="notificationTitle">{t('pwa.title')}</Label>
          <Input
            id="notificationTitle"
            value={testNotificationPayload.title}
            onChange={(e) => setTestNotificationPayload(prev => ({ ...prev, title: e.target.value }))}
          />
        </FormGroup>
        <FormGroup>
          <Label for="notificationBody">{t('pwa.body')}</Label>
          <Input
            id="notificationBody"
            type="textarea"
            value={testNotificationPayload.body}
            onChange={(e) => setTestNotificationPayload(prev => ({ ...prev, body: e.target.value }))}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowTestNotification(false)}>
          {t('common.cancel')}
        </Button>
        <Button color="primary" onClick={handleTestNotification}>
          {t('pwa.send_test_notification')}
        </Button>
      </ModalFooter>
    </Modal>
  );

  // Add Cache Config Modal
  const cacheConfigModal = (
    <Modal isOpen={showCacheModal} toggle={() => setShowCacheModal(false)} size="lg">
      <ModalHeader toggle={() => setShowCacheModal(false)}>
        <i className="cil-plus c-icon-white mr-2" />
        {t('pwa.add_cache_configuration')}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="cacheName">{t('pwa.cache_name')}</Label>
              <Input
                id="cacheName"
                value={newCacheConfig.name}
                onChange={(e) => setNewCacheConfig(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., custom-cache"
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="cacheStrategy">{t('pwa.strategy')}</Label>
              <Input
                id="cacheStrategy"
                type="select"
                value={newCacheConfig.strategy?.name}
                onChange={(e) => setNewCacheConfig(prev => ({ 
                  ...prev, 
                  strategy: { ...prev.strategy, name: e.target.value as any }
                }))}
              >
                <option value="CacheFirst">{t('pwa.cache_first')}</option>
                <option value="NetworkFirst">{t('pwa.network_first')}</option>
                <option value="StaleWhileRevalidate">{t('pwa.stale_while_revalidate')}</option>
                <option value="NetworkOnly">{t('pwa.network_only')}</option>
                <option value="CacheOnly">{t('pwa.cache_only')}</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label for="cachePatterns">{t('pwa.url_patterns_one_per_line')}</Label>
          <Input
            id="cachePatterns"
            type="textarea"
            value={newCacheConfig.patterns?.join('\n')}
            onChange={(e) => setNewCacheConfig(prev => ({ 
              ...prev, 
              patterns: e.target.value.split('\n').filter(p => p.trim())
            }))}
            placeholder="/api/users/**/*&#10;/assets/images/**/*"
            rows={3}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowCacheModal(false)}>
          {t('common.cancel')}
        </Button>
        <Button color="primary" onClick={handleAddCacheConfig}>
          {t('pwa.add_configuration')}
        </Button>
      </ModalFooter>
    </Modal>
  );

  // Add Widget Modal
  const widgetModal = (
    <Modal isOpen={showWidgetModal} toggle={() => setShowWidgetModal(false)} size="lg">
      <ModalHeader toggle={() => setShowWidgetModal(false)}>
        <i className="cil-plus c-icon-white mr-2" />
        {t('pwa.add_widget')}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="widgetId">{t('pwa.widget_id')}</Label>
              <Input
                id="widgetId"
                value={newWidget.id}
                onChange={(e) => setNewWidget(prev => ({ ...prev, id: e.target.value }))}
                placeholder="e.g., sales-widget"
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="widgetName">{t('pwa.widget_name')}</Label>
              <Input
                id="widgetName"
                value={newWidget.name}
                onChange={(e) => setNewWidget(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sales Widget"
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label for="widgetDescription">{t('pwa.description')}</Label>
          <Input
            id="widgetDescription"
            type="textarea"
            value={newWidget.description}
            onChange={(e) => setNewWidget(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Widget description"
            rows={2}
          />
        </FormGroup>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="widgetSize">{t('pwa.size')}</Label>
              <Input
                id="widgetSize"
                type="select"
                value={newWidget.size}
                onChange={(e) => setNewWidget(prev => ({ ...prev, size: e.target.value as any }))}
              >
                <option value="small">{t('pwa.small')}</option>
                <option value="medium">{t('pwa.medium')}</option>
                <option value="large">{t('pwa.large')}</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="widgetUpdateInterval">{t('pwa.update_interval_seconds')}</Label>
              <Input
                id="widgetUpdateInterval"
                type="number"
                value={newWidget.updateInterval}
                onChange={(e) => setNewWidget(prev => ({ ...prev, updateInterval: parseInt(e.target.value) }))}
                min="5"
                max="3600"
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label for="widgetUrl">{t('pwa.widget_url')}</Label>
          <Input
            id="widgetUrl"
            value={newWidget.url}
            onChange={(e) => setNewWidget(prev => ({ ...prev, url: e.target.value }))}
            placeholder="e.g., /api/widgets/sales"
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowWidgetModal(false)}>
          {t('common.cancel')}
        </Button>
        <Button color="primary" onClick={handleAddWidget}>
          {t('pwa.add_widget')}
        </Button>
      </ModalFooter>
    </Modal>
  );

  // Add Shortcut Modal
  const shortcutModal = (
    <Modal isOpen={showShortcutModal} toggle={() => setShowShortcutModal(false)} size="lg">
      <ModalHeader toggle={() => setShowShortcutModal(false)}>
        <i className="cil-plus c-icon-white mr-2" />
        {t('pwa.add_app_shortcut')}
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="shortcutName">{t('pwa.shortcut_name')}</Label>
              <Input
                id="shortcutName"
                value={newShortcut.name}
                onChange={(e) => setNewShortcut(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sales Dashboard"
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="shortcutShortName">{t('pwa.short_name')}</Label>
              <Input
                id="shortcutShortName"
                value={newShortcut.shortName}
                onChange={(e) => setNewShortcut(prev => ({ ...prev, shortName: e.target.value }))}
                placeholder="e.g., Sales"
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label for="shortcutDescription">{t('pwa.description')}</Label>
          <Input
            id="shortcutDescription"
            type="textarea"
            value={newShortcut.description}
            onChange={(e) => setNewShortcut(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Shortcut description"
            rows={2}
          />
        </FormGroup>
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="shortcutIcon">{t('pwa.icon')}</Label>
              <Input
                id="shortcutIcon"
                value={newShortcut.icon}
                onChange={(e) => setNewShortcut(prev => ({ ...prev, icon: e.target.value }))}
                placeholder="e.g., /assets/icons/sales.png"
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="shortcutColor">{t('pwa.color')}</Label>
              <Input
                id="shortcutColor"
                type="color"
                value={newShortcut.color}
                onChange={(e) => setNewShortcut(prev => ({ ...prev, color: e.target.value }))}
              />
            </FormGroup>
          </Col>
        </Row>
        <FormGroup>
          <Label for="shortcutUrl">{t('pwa.shortcut_url')}</Label>
          <Input
            id="shortcutUrl"
            value={newShortcut.url}
            onChange={(e) => setNewShortcut(prev => ({ ...prev, url: e.target.value }))}
            placeholder="e.g., /sales/dashboard"
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={() => setShowShortcutModal(false)}>
          {t('common.cancel')}
        </Button>
        <Button color="primary" onClick={handleAddShortcut}>
          {t('pwa.add_shortcut')}
        </Button>
      </ModalFooter>
    </Modal>
  );

  // If modalProps are provided, wrap in modal
  if (modalProps) {
    return (
      <Modal {...modalProps}>
        <ModalHeader>
          <i className="cil-settings" />
          <strong>{t('pwa.title')}</strong>
        </ModalHeader>
        <ModalBody>
          {content}
          {testNotificationModal}
          {cacheConfigModal}
          {widgetModal}
          {shortcutModal}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" className="mr-1 d-flex" onClick={handleCancel}>
            <i className="cil-x-circle icons w-icon-30 c-icon-red" />
            {t('common:cancel')}
          </Button>
          <Button 
            color="primary" 
            className="mr-1 d-flex" 
            onClick={applySyncConfig}
            disabled={isApplying}
          >
            <i className="icon-check icons w-icon-30" />
            {isApplying ? (
                <>
                  <Spinner size="sm" className="mr-1" />
                  {t('common:saving')}
                </>
              ) : (
                 t('common:save_changes')
              )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  // Return content without modal if no modalProps
  return (
    <>
      {content}
      {testNotificationModal}
      {cacheConfigModal}
      {widgetModal}
      {shortcutModal}
    </>
  );
};

export default SyncTimingConfig;
